import {
    Chat,
    ChartLine,
    ChartColumn,
    DataTable,
    Edit,
    Help,
    Rss,
    SummaryKpi,
    Sun,
    Template,
    Time,
    TrashCan,
    UserMultiple,
    Video,
} from '@carbon/icons-react';
import Fade from '@mui/material/Fade';
import Tooltip from '@mui/material/Tooltip';
import { IconButton } from '@mui/material';
import cloneDeep from 'lodash/cloneDeep';
import { CSSProperties, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { filter } from 'rxjs/operators';
import { useStream } from '../../hooks/useStream';
import { RoleEnum, StageViewModeEnum, WidgetData, WidgetTypeEnum } from '../../models/Index';
import DoormanService from '../../services/doormanService/DoormanService';
import StateService from '../../services/stateService/StateService';
import DemoIndicator from '../demo-indicator/DemoIndicator';
import WidgetContent from '../widget-content/WidgetContent';
import Dashboard from '../../pages/dashboard/Dashboard';
import { useStyles } from './styles';
import SgtaService from '../../services/sgtaService/SgtaService';

type Props = {
    widgetData: WidgetData;
    openSettings: (any) => void;
    removeWidget: (any) => void;
};

function WidgetWrapper(props: Props) {
    const classes = useStyles();
    const [widgetData, setWidgetData] = useState({} as WidgetData);
    const [isLinkoutActive, setIsLinkoutActive] = useState(false);
    const [widgetTitle, setWidgetTitle] = useState('');
    const [t] = useTranslation();
    const [stageViewMode, setStageViewMode] = useState(StageViewModeEnum.PUBLISHED as StageViewModeEnum);
    const [currentRoles, setCurrentRoles] = useState([] as RoleEnum[]);
    const [rolesPresent, setRolesPresent] = useState(false);
    const [titleFontSize, setTitleFontSize] = useState('1.5rem');
    const [titlePaddingBottom, setTitlePaddingBottom] = useState('16px');
    const [singleValueStyles, setSingleValueStyles] = useState({} as CSSProperties);
    const [themeName, setThemeName] = useState('');
    const bodyTheme = localStorage.getItem('theme'); // retrieve the value from local storage, default to 'light'
    const [isEditorOpen, setIsEditorOpen] = useStream(StateService.isEditorOpen$);
    const [guestToken, setGuestToken] = useState<string>('');
    const abortController = new AbortController();
    const [currentCustomerUuid, setCurrentCustomerUuid] = useStream(StateService.currentCustomerUuid$);

    useEffect(() => {
        const currentRoles$ = StateService.currentRoles$.subscribe((currentRoles) => {
            setCurrentRoles(currentRoles);
        });

        const isLinkoutActive$ = StateService.isLinkoutActive$.subscribe((isLinkoutActive) => {
            setIsLinkoutActive(isLinkoutActive);
        });

        const stageViewMode$ = StateService.stageViewMode$.subscribe((mode) => {
            setStageViewMode(mode);
        });

        const darkTheme$ = StateService.darkTheme$.subscribe((theme) => {
            setThemeName(theme);
        });

        return () => {
            currentRoles$.unsubscribe();
            isLinkoutActive$.unsubscribe();
            stageViewMode$.unsubscribe();
            darkTheme$.unsubscribe();
        };
    });

    useEffect(() => {
        setWidgetData(props?.widgetData);
        if (props?.widgetData?.type === WidgetTypeEnum.SUPERSET_DASHBOARD && props?.widgetData?.supersetUuid) {
            generateGuestToken(props.widgetData.supersetUuid);
        }
    }, [props?.widgetData, props?.widgetData?.supersetUuid]);

    const generateGuestToken = async (supersetUuid: string) => {
        try {
            if (currentCustomerUuid) {
                const response = await SgtaService.getGuesttoken(supersetUuid, currentCustomerUuid, {
                    signal: abortController.signal,
                });
                setGuestToken(response?.data?.guest_token);
            }
        } catch (error) {
            console.error('Error generating guest token:', error);
        }
    };

    useEffect(() => {
        if (widgetData?.data?.length > 0 && widgetData?.data[0]?.setLocationAsTitle) {
            setWidgetTitle(widgetData?.data[0]?.location?.name);
        } else {
            setWidgetTitle(widgetData?.title);
        }

        const singleValueStyles$ = StateService.singleValueStyles$
            .pipe(
                filter(
                    (singleValueStylesObj) =>
                        singleValueStylesObj.widgetKey === widgetData?.key && widgetData?.type === WidgetTypeEnum.SINGLE_VALUE,
                ),
            )
            .subscribe((singleValueStylesObj) => {
                setSingleValueStyles(singleValueStylesObj.styles);
            });

        return () => {
            singleValueStyles$.unsubscribe();
        };
    }, [widgetData?.data, widgetData?.title]);

    useEffect(() => {
        if (widgetData.titleFontSize) {
            setTitleFontSize(widgetData.titleFontSize);
        }
    }, [widgetData.titleFontSize]);

    useEffect(() => {
        setTitlePaddingBottom(titleFontSize !== '1.8rem' ? '16px' : '32px');
        if ([WidgetTypeEnum.FREE_TEXT].includes(widgetData.type)) {
            setTitlePaddingBottom('16px');
        }
        if ([WidgetTypeEnum.IFRAME, WidgetTypeEnum.PICTURE].includes(widgetData.type)) {
            setTitlePaddingBottom('unset');
        }
        if ([WidgetTypeEnum.VIDEO].includes(widgetData.type)) {
            setTitlePaddingBottom('16px');
        }
    }, [titleFontSize, widgetData.type]);

    useEffect(() => {
        setRolesPresent(currentRoles.length > 0 && currentRoles[0].length > 0);
    }, [currentRoles]);

    function openSettings() {
        props.openSettings(cloneDeep(widgetData));
        setIsEditorOpen(true);
    }

    function removeWidget() {
        props.removeWidget(widgetData);
    }

    const styles = {
        backgroundColor: singleValueStyles.backgroundColor ? singleValueStyles.backgroundColor : `${widgetData.backgroundColor}`,
        padding: [WidgetTypeEnum.IFRAME, WidgetTypeEnum.PICTURE].includes(widgetData.type) ? '0' : '16px',
        borderLeft: widgetData.isBorderColor ? `8px solid ${widgetData.borderColor}` : 0,
    };

    const headlineStyles = {
        backgroundColor: singleValueStyles.backgroundColor ? singleValueStyles.backgroundColor : `${widgetData.backgroundColor}`,
        padding: '8px 16px',
        borderLeft: widgetData.isBorderColor ? `8px solid ${widgetData.borderColor}` : 0,
    };

    const widgetDataTitleStyles = {
        fontSize: widgetData?.titleFontSize,
    };

    return (
        // remove padding if use as headline is true
        <div key={widgetData?.key} className={classes.widget} style={widgetData?.isUsedAsHeadline ? headlineStyles : styles}>
            <>
                {widgetData?.isUsingDefault && ![WidgetTypeEnum.SUPERSET_DASHBOARD].includes(widgetData.type) && (
                    <div
                        className={
                            stageViewMode === StageViewModeEnum.PUBLISHED ? classes.demoIndicatorWrapperPub : classes.demoIndicatorWrapper
                        }
                    >
                        <DemoIndicator />
                    </div>
                )}
                {!isLinkoutActive &&
                    stageViewMode === StageViewModeEnum.WORK_IN_PROGRESS &&
                    rolesPresent &&
                    DoormanService.isAccessGranted('widgetEditing', currentRoles) && (
                        <div className={classes.widgetButtons}>
                            <Tooltip
                                classes={{
                                    tooltip: classes.tooltip,
                                }}
                                title={t('COMMON.WIDGET_SETTINGS_TOOLTIP')}
                                placement="left-start"
                                TransitionComponent={Fade}
                                arrow
                            >
                                <IconButton
                                    data-testid="edit-widget"
                                    onClick={openSettings}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    size="small"
                                >
                                    <Edit />
                                </IconButton>
                            </Tooltip>
                            <Tooltip
                                classes={{
                                    tooltip: classes.tooltip,
                                }}
                                title={t('COMMON.WIDGET_REMOVE_TOOLTIP')}
                                placement="left-start"
                                TransitionComponent={Fade}
                                arrow
                            >
                                <IconButton
                                    data-testid="remove-widget"
                                    onClick={removeWidget}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    size="small"
                                >
                                    <TrashCan />
                                </IconButton>
                            </Tooltip>
                        </div>
                    )}
            </>
            {widgetTitle?.length > 0 &&
                ![WidgetTypeEnum.IFRAME, WidgetTypeEnum.PICTURE, WidgetTypeEnum.FREE_TEXT].includes(widgetData.type) && (
                    <div className={classes.widgetTitleWrapper}>
                        <div
                            className={
                                widgetData?.type === WidgetTypeEnum.WEATHER
                                    ? [classes.pb8, classes.widgetIcon].join(' ')
                                    : classes.widgetIcon
                            }
                        >
                            {widgetData.type === WidgetTypeEnum.ACD_WALLBOARD_BOTTOM && <UserMultiple />}
                            {widgetData.type === WidgetTypeEnum.ACD_WALLBOARD_RIGHT && <UserMultiple />}
                            {widgetData.type === WidgetTypeEnum.CLOCK && <Time />}
                            {widgetData.type === WidgetTypeEnum.FREE_TEXT && <Chat />}
                            {widgetData.type === WidgetTypeEnum.OBM_CAMPAIGN_OVERVIEW && <UserMultiple />}
                            {widgetData.type === WidgetTypeEnum.OBM_USER_RANKING && <UserMultiple />}
                            {widgetData.type === WidgetTypeEnum.RSS_FEED && <Rss />}
                            {widgetData.type === WidgetTypeEnum.SINGLE_VALUE && <SummaryKpi />}
                            {widgetData.type === WidgetTypeEnum.SUPERSET_DASHBOARD && <Template />}
                            {widgetData.type === WidgetTypeEnum.VIDEO && <Video />}
                            {widgetData.type === WidgetTypeEnum.WEATHER && <Sun />}
                            {widgetData.type === WidgetTypeEnum.LINE_CHART && <ChartLine />}
                            {widgetData.type === WidgetTypeEnum.BAR_CHART && <ChartColumn />}
                            {widgetData.type === WidgetTypeEnum.DATA_TABLE && <DataTable />}
                            {widgetData.type === WidgetTypeEnum.NOT_SELECTED && <Help />}
                        </div>
                        {/* remove widget title if input field is empty */}
                        {widgetTitle?.length && (
                            <div
                                className={
                                    widgetData?.type === WidgetTypeEnum.WEATHER
                                        ? [classes.pb8, classes.widgetTitle].join(' ')
                                        : classes.widgetTitle
                                }
                                style={widgetDataTitleStyles}
                            >
                                {widgetData?.data[0]?.setLocationAsTitle ? widgetData?.data[0]?.location.name : widgetTitle}
                            </div>
                        )}
                    </div>
                )}
            {widgetData.type === WidgetTypeEnum.SUPERSET_DASHBOARD && widgetData.dashboardId ? (
                <Dashboard />
            ) : (
                <WidgetContent data={widgetData} />
            )}
        </div>
    );
}

export default WidgetWrapper;
